import {
  CodeItem,
  DateGroupDef,
  DateLabelOverrides,
  RosPeSavePlan,
  SavedRosPe,
  TemplateTypeSet,
} from "./initial-f2f-types";
import { PhysicalROSTemplateTemplateTypeEnum } from "@/services";
import {
  OptionGroupDef,
  PharmacyEntry,
  SectionGroupDef,
  SummaryLine,
  buildFreeTextEntry,
  formatSummaryLine,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";

export const createChooseOneGroups = (
  titles: Record<string, string>,
  options: string[],
  withFreeTextOnLast = false
): OptionGroupDef[] => {
  const entries = Object.entries(titles);
  return entries.map(([key, title], index) => ({
    key,
    title,
    options,
    chooseOne: true,
    isInline: true,
    withFreeText: withFreeTextOnLast && index === entries.length - 1,
  }));
};

/** One free-text line per entry; an option-less group renders as a single text box. */
export const createTextGroups = (
  titles: Record<string, string>
): OptionGroupDef[] =>
  Object.entries(titles).map(([key, title]) => ({
    key,
    title,
    options: [],
    withDetails: false,
  }));

export const withHeading = (
  heading: string,
  groups: OptionGroupDef[]
): OptionGroupDef[] => groups.map((group) => ({ ...group, heading }));

export const toCustomCode = (text: string): CodeItem | undefined => {
  const trimmed = text.trim();
  return trimmed
    ? { value: trimmed, code: trimmed, isCustom: true }
    : undefined;
};

/** Keeps the first of any codes sharing a value or a code label. */
export const uniqueCodes = (codes: CodeItem[]): CodeItem[] =>
  codes.filter(
    (code, index) =>
      codes.findIndex(
        (it) => it.value === code.value || it.code === code.code
      ) === index
  );

export const formatIsoDate = (iso: string): string => {
  const [year, month, day] = iso.split("-");
  return year && month && day ? `${month}/${day}/${year}` : iso;
};

/** Catalog codes come as "CODE - description"; typed entries have no code part. */
export const splitCode = (code: string): { code: string; name: string } => {
  const [head, ...rest] = code.split(" - ");
  return rest.length
    ? { code: head.trim(), name: rest.join(" - ").trim() }
    : { code: "", name: code.trim() };
};

export const formatCode = (item: CodeItem): string => {
  const notes = item.notes?.trim();
  return notes ? `${item.code} (${notes})` : item.code;
};

const buildCodeEntries = (
  label: string,
  codes: CodeItem[],
  isNumbered: boolean
): SummaryLine[] => {
  if (!codes.length) return [];
  if (!isNumbered) {
    return [{ title: label, text: `${codes.map(formatCode).join(", ")}.` }];
  }
  return [
    { title: label, text: "" },
    ...codes.map((code, index) => `${index + 1}. ${formatCode(code)}`),
  ];
};

export const buildCodeSummaryEntries = (
  label: string,
  codes: CodeItem[],
  freeText: string,
  isNumbered = false
): SummaryLine[] => [
  ...buildCodeEntries(label, codes, isNumbered),
  ...buildFreeTextEntry(freeText),
];

export const buildCodeSummaryLines = (
  label: string,
  codes: CodeItem[],
  freeText: string,
  isNumbered = false
): string[] =>
  buildCodeSummaryEntries(label, codes, freeText, isNumbered).map(
    formatSummaryLine
  );

export const applyDateLabels = (
  group: DateGroupDef,
  labels: DateLabelOverrides = {}
): DateGroupDef => ({
  ...group,
  fields: group.fields.map((field) => ({
    ...field,
    label: labels[field.key] ?? field.label,
  })),
});

export const buildDateSummaryEntries = (
  group: DateGroupDef,
  values: Record<string, string>
): SummaryLine[] =>
  group.fields
    .filter((field) => values[field.key])
    .map((field) => ({
      title: field.label,
      text: `${formatIsoDate(values[field.key])}.`,
    }));

export const buildDateSummaryLines = (
  group: DateGroupDef,
  values: Record<string, string>
): string[] => buildDateSummaryEntries(group, values).map(formatSummaryLine);

const toRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null
    ? Object.fromEntries(Object.entries(value))
    : {};

const toText = (value: unknown): string =>
  typeof value === "string" ? value : "";

/** The encounter-template list is typed as { name, uuid } but returns the saved ROS/PE rows. */
export const toSavedRosPe = (content: unknown): SavedRosPe[] =>
  Array.isArray(content)
    ? content.flatMap((item) => {
        const record = toRecord(item);
        const uuid = toText(record.uuid);
        const name = toText(record.templateNameType);
        return uuid && name && record.archive !== true
          ? [{ uuid, name, description: toText(record.description) }]
          : [];
      })
    : [];

export const mergeSavedIntoSystems = (
  base: TemplateTypeSet,
  saved: SavedRosPe[]
): TemplateTypeSet =>
  base.map((system) => {
    const hit = saved.find((row) => row.name === system.name);
    return hit
      ? { ...system, value: hit.description, isSelected: true }
      : system;
  });

export const buildRosPeSavePlan = (
  systems: TemplateTypeSet,
  saved: SavedRosPe[],
  templateType: PhysicalROSTemplateTemplateTypeEnum
): RosPeSavePlan => {
  const plan: RosPeSavePlan = { create: [], update: [], archiveIds: [] };
  systems.forEach((system) => {
    const name = system.name ?? "";
    const description = system.value?.trim() ?? "";
    const existing = saved.find((row) => row.name === name);
    const isFilled = !!description && system.isSelected !== false;
    if (existing && !isFilled) plan.archiveIds.push(existing.uuid);
    else if (existing && existing.description !== description)
      plan.update.push({
        uuid: existing.uuid,
        templateNameType: name,
        templateType,
        description,
        active: true,
      });
    else if (!existing && isFilled)
      plan.create.push({
        templateNameType: name,
        templateType,
        description,
        active: true,
      });
  });
  return plan;
};

export const buildPharmacyLines = (pharmacies: PharmacyEntry[]): string[] =>
  pharmacies
    .filter((it) => it.name.trim())
    .map((it) =>
      [
        it.name.trim(),
        it.phone.trim(),
        it.fax.trim() && `fax ${it.fax.trim()}`,
        it.address.trim(),
      ]
        .filter(Boolean)
        .join(", ")
    );

export const filterVisibleSections = (
  groups: SectionGroupDef[],
  hiddenKeys: string[] = [],
  titleOverrides: Record<string, string> = {}
): SectionGroupDef[] =>
  groups
    .map((group) => ({
      ...group,
      sections: group.sections
        .filter((section) => !hiddenKeys.includes(section.key))
        .map((section) => ({
          ...section,
          title: titleOverrides[section.key] ?? section.title,
        })),
    }))
    .filter((group) => group.sections.length);

/** Codes picked from the catalog, in the `{ value, code }` shape of the encounter's assessment and procedure codes. */
export const toStructuredCodes = (codes: CodeItem[]): CodeItem[] =>
  codes
    .filter((it) => !it.isCustom)
    .map(({ value, code }) => ({ value, code }));
